// stores/cookieWow.js
import { defineStore } from "pinia";

// Share in-flight/completed loads across layout remounts. Failed loads can retry.
const scriptLoads = new Map();
let consentListenerAttached = false;
let lastConsentState;
let bannerInitializationHandled = false;
let consentDefaultsInitialized = false;

function gtag() {
  window.dataLayer.push(arguments);
}

function initializeConsentDefaults() {
  window.dataLayer = window.dataLayer || [];
  if (consentDefaultsInitialized) return;

  // Consent Mode must be configured before any Google tag is loaded. CookieWow
  // can then update these values after restoring or changing the user's choice.
  gtag("consent", "default", {
    ad_storage: "denied",
    analytics_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
  consentDefaultsInitialized = true;
}

function initializeCookieWowBanner() {
  if (bannerInitializationHandled) return;
  // CookieWow's config registers a DOMContentLoaded listener without checking
  // readyState. When loaded after Nuxt mounts, that event has already passed.
  // Use the SDK's normal initialization (which respects saved consent), once.
  if (document.readyState !== "loading") {
    window.cwcCookieBanner?.reinitializeBanner?.();
  }
  // While loading, the SDK's own DOMContentLoaded listener handles the banner.
  bannerInitializationHandled = true;
}

function syncConsentWithGtm() {
  if (typeof window.cwcIsUserAccept !== "function") return;

  const state = JSON.stringify([
    window.cwcIsUserAccept("analytics"),
    window.cwcIsUserAccept("marketing"),
  ]);
  if (state === lastConsentState) return;
  lastConsentState = state;

  const analyticsGranted = window.cwcIsUserAccept("analytics");
  const marketingGranted = window.cwcIsUserAccept("marketing");
  gtag("consent", "update", {
    analytics_storage: analyticsGranted ? "granted" : "denied",
    ad_storage: marketingGranted ? "granted" : "denied",
    ad_user_data: marketingGranted ? "granted" : "denied",
    ad_personalization: marketingGranted ? "granted" : "denied",
  });

  // The published SIT-GCM tag reads cwcIsUserAccept() on this legacy event.
  // CookieWow emits CookieWowConsentUpdated, which that trigger does not match.
  // Notify GTM only; the SDK remains the source of the user's consent choices.
  window.dataLayer.push({ event: "SITConsentUpdated" });
}

function connectCookieWowConsent() {
  window.dataLayer = window.dataLayer || [];
  if (!consentListenerAttached && window.cwcSDK?.addEventListener) {
    window.cwcSDK.addEventListener("consent", syncConsentWithGtm);
    consentListenerAttached = true;
  }
  // Cover saved choices that the SDK restored before the listener was attached.
  // If restoration is still pending, the SDK's consent callback handles it.
  syncConsentWithGtm();
}

function loadScript(src, id, attributes = {}) {
  if (scriptLoads.has(id)) return scriptLoads.get(id);

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    const nonce = document.querySelector("script[nonce]")?.nonce;
    if (nonce) script.nonce = nonce;
    for (const [name, value] of Object.entries(attributes)) {
      script.setAttribute(name, value);
    }
    script.onload = resolve;
    script.onerror = () => {
      script.remove();
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.append(script);
  }).catch((error) => {
    scriptLoads.delete(id);
    throw error;
  });

  scriptLoads.set(id, promise);
  return promise;
}

export const useCookieWowStore = defineStore("cookieWow", {
  state: () => ({
    cookieWowId: null,
    gtm: null,
    isLoading: false,
    isShow: false,
    error: null,
  }),
  actions: {
    async fetchCookieWowId() {
      this.isLoading = true;
      this.error = null;

      const { baseApi } = useApiBase();

      try {
        // Fetch the public CookieWow and GTM configuration.
        const response = await $fetch(
          `${baseApi}/en/consent/cookiewow`,
        );

        this.cookieWowId = response.data.cookie_wow;
        this.gtm = response.data.gtm;
      } catch (err) {
        this.error =
          err.message || "An error occurred while fetching the CookieWow ID.";
      } finally {
        this.isLoading = false;
      }
    },

    async injectCookieWowScript(id, gtm) {
      if (!id || !/^GTM-[A-Z0-9]+$/.test(gtm || '')) {
        this.error = "Missing CookieWow ID or invalid GTM container ID.";
        return;
      }

      try {
        initializeConsentDefaults();
        // The configuration calls window.cwcSDK.init(), so the SDK must finish
        // loading first. Do not fetch the config via XHR or execute it twice.
        await loadScript("https://cookiecdn.com/cwc.js", "cookie-wow-library");
        await loadScript(
          `https://cookiecdn.com/configs/${encodeURIComponent(id)}`,
          "cookieWow",
          { "data-cwcid": id },
        );
        connectCookieWowConsent();
        initializeCookieWowBanner();

        // Execute the bootstrap in this trusted module, not an inline script
        // that can be blocked by the site's nonce-based CSP.
        if (!scriptLoads.has("gtm-script")) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
        }
        await loadScript(
          `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtm)}`,
          "gtm-script",
        );
        this.error = null;
      } catch (error) {
        this.error = error.message || "Error loading CookieWow or GTM scripts.";
        console.error("Error loading scripts:", error);
      }
    },
  },
});
